<template>
  <div class="subscription-page">
    <!-- Header simplificado -->
    <v-app-bar
      app
      color="white"
      class="ma-2 mt-0 rounded-br-xl rounded-bl-xl pr-2 no-print"
      elevation="1"
    >
      <v-row align="center" justify="center">
        <v-col cols="12">
          <h3 class="text-center">Contratar Linha de Rastreio Adicional</h3>
        </v-col>
      </v-row>
      <div class="user-menu"><user-menu /></div>
    </v-app-bar>

    <!-- Background com gradiente suave -->
    <div class="background-gradient"></div>

    <v-container class="subscription-container" fluid>
      <!-- Seção de planos disponíveis -->
      <v-row>
        <v-col cols="12">
          <div class="plans-header mb-4">
            <h3 class="title mb-2">Escolha suas linhas de rastreio</h3>
            <p class="subtitle-1 grey--text">
              Selecione as linhas que deseja adicionar à sua assinatura atual
            </p>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <!-- Estado de carregamento -->
          <div v-if="loadingPlans" class="text-center pa-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              class="mb-4"
            ></v-progress-circular>
            <h3 class="grey--text">
              Carregando assinaturas adicionais disponíveis...
            </h3>
            <p class="grey--text">
              Aguarde enquanto carregamos as assinaturas adicionais para você.
            </p>
          </div>
          <!-- Nenhum plano disponível -->
          <div v-else-if="availablePlans.length === 0" class="text-center pa-8">
            <v-icon color="grey" size="64" class="mb-4"
              >mdi-package-variant</v-icon
            >
            <h3 class="grey--text">Nenhuma assinatura adicional disponível</h3>
            <p class="grey--text">
              Não há assinaturas adicionais disponíveis no momento.
            </p>
            <v-btn color="primary" class="mt-4" @click="fetchAddonPlans">
              <v-icon left>mdi-refresh</v-icon>
              Tentar novamente
            </v-btn>
          </div>
          <!-- Grid de planos -->
          <v-row v-else class="plans-grid">
            <v-col
              v-for="plan in availablePlans"
              :key="plan.idPaypal"
              cols="12"
              sm="6"
              md="6"
              lg="4"
            >
              <v-card
                :class="[
                  'plan-card elevation-3',
                  { 'selected-card': isPlanSelected(plan) },
                ]"
                :style="getCardStyle(plan)"
                @click="togglePlanSelection(plan)"
              >
                <!-- Indicador de seleção -->
                <div class="selection-indicator">
                  <v-fade-transition>
                    <v-icon
                      v-if="isPlanSelected(plan)"
                      color="success"
                      class="selection-check"
                    >
                      mdi-check-circle
                    </v-icon>
                  </v-fade-transition>
                </div>

                <!-- Ícone de informação -->
                <div class="info-indicator">
                  <v-tooltip bottom max-width="320">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="grey lighten-1"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop=""
                      >
                        <v-icon small>mdi-help-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <div class="pa-2">
                      <div class="font-weight-bold mb-2">{{ plan.name }}</div>
                      <div class="mb-2">{{ plan.description }}</div>
                    </div>
                  </v-tooltip>
                </div>

                <v-card-text
                  class="pa-4 text-center d-flex flex-column h-100"
                  style="height: 100%"
                >
                  <!-- Ícone do plano -->
                  <div
                    class="plan-icon-large mb-3"
                    :style="{ background: plan.iconColor || '#1AE06E' }"
                  >
                    <v-icon color="white" size="32">{{ plan.icon }}</v-icon>
                  </div>

                  <!-- Nome do plano -->
                  <h4 class="subtitle-1 font-weight-bold mb-2">
                    {{ plan.name }}
                  </h4>

                  <!-- Preço -->
                  <div class="price-section mb-3">
                    <div
                      class="price-amount primary--text font-weight-bold headline"
                    >
                      {{ formatCurrency(plan.price) }}
                    </div>
                    <div class="caption grey--text">por mês</div>

                    <!-- Trial badge -->
                    <div
                      v-if="plan.trialDays && plan.trialDays > 0"
                      class="mt-2"
                    >
                      <v-chip small color="success" outlined>
                        <v-icon small left>mdi-gift</v-icon>
                        {{ plan.trialDays }} dias grátis
                      </v-chip>
                    </div>
                  </div>

                  <!-- Descrição -->
                  <p
                    :class="`caption grey--text mb-3 ${
                      plan.trialDays && plan.trialDays > 0 ? '' : 'mt-7'
                    }`"
                  >
                    {{ plan.shortDescription }}
                  </p>

                  <!-- Funcionalidades principais -->
                  <div class="features-preview mb-4">
                    <div
                      v-for="(feature, index) in plan.benefits?.slice(0, 5)"
                      :key="index"
                      class="feature-item caption d-flex align-center mb-1"
                    >
                      <v-icon color="success" size="14" class="mr-2">
                        mdi-check
                      </v-icon>
                      <span>{{ feature }}</span>
                    </div>
                    <div
                      v-if="plan.benefits?.length > 5"
                      class="caption grey--text"
                    >
                      + {{ plan.benefits.length - 5 }} outras funcionalidades
                    </div>
                  </div>

                  <!-- Tags -->
                  <div v-if="plan.tags" class="tags-section mb-3">
                    <div class="d-flex flex-wrap justify-center">
                      <v-chip
                        v-for="(tag, tagIndex) in plan.tags.split('\n')"
                        :key="tagIndex"
                        x-small
                        outlined
                        color="primary"
                        class="ma-1"
                      >
                        {{ tag }}
                      </v-chip>
                    </div>
                  </div>

                  <!-- Botão de ação -->
                  <div class="mt-auto">
                    <v-btn
                      v-if="canSelectPlans && !isAddonInMainSubscription(plan)"
                      style="height: 36px"
                      :color="isPlanSelected(plan) ? 'success' : 'primary'"
                      :outlined="!isPlanSelected(plan)"
                      block
                      @click.stop="togglePlanSelection(plan)"
                    >
                      <v-icon left small>
                        {{ isPlanSelected(plan) ? "mdi-check" : "mdi-plus" }}
                      </v-icon>
                      {{ isPlanSelected(plan) ? "Selecionado" : "Selecionar" }}
                    </v-btn>
                    <v-btn
                      v-else-if="isAddonInMainSubscription(plan)"
                      color="grey"
                      outlined
                      block
                      small
                      disabled
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Já Contratado
                    </v-btn>
                    <v-btn
                      v-else
                      color="grey"
                      outlined
                      block
                      small
                      disabled
                      @click.stop="showManualSubscriptionAlert"
                    >
                      <v-icon left small>mdi-lock</v-icon>
                      Assinatura via suporte
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- Como funciona -->
      <v-row v-if="availablePlans.length > 0">
        <v-col cols="12">
          <v-card class="info-section elevation-1 mt-8 mb-8">
            <v-card-text class="pa-6">
              <div class="text-center mb-6">
                <h3 class="title font-weight-medium primary--text mb-3">
                  Como Funciona?
                </h3>
                <p class="body-1 grey--text text--darken-1">
                  As linhas adicionais são cobradas junto com sua assinatura
                  principal
                </p>
              </div>

              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <div class="feature-step text-center">
                    <v-avatar color="primary" size="56" class="mb-3">
                      <span class="white--text headline font-weight-medium">
                        1
                      </span>
                    </v-avatar>
                    <h4 class="subtitle-1 font-weight-medium mb-2">
                      Escolha suas linhas
                    </h4>
                    <p class="caption grey--text text--darken-1">
                      Selecione quantas linhas desejar
                    </p>
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <div class="feature-step text-center">
                    <v-avatar color="success" size="56" class="mb-3">
                      <span class="white--text headline font-weight-medium">
                        2
                      </span>
                    </v-avatar>
                    <h4 class="subtitle-1 font-weight-medium mb-2">
                      Teste grátis
                    </h4>
                    <p class="caption grey--text text--darken-1">
                      Muitas linhas oferecem períodos gratuitos para você testar
                    </p>
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <div class="feature-step text-center">
                    <v-avatar color="orange" size="56" class="mb-3">
                      <span class="white--text headline font-weight-medium">
                        3
                      </span>
                    </v-avatar>
                    <h4 class="subtitle-1 font-weight-medium mb-2">
                      Uma só fatura
                    </h4>
                    <p class="caption grey--text text--darken-1">
                      Tudo é cobrado junto com sua assinatura principal
                    </p>
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <div class="feature-step text-center">
                    <v-avatar color="blue" size="56" class="mb-3">
                      <span class="white--text headline font-weight-medium">
                        4
                      </span>
                    </v-avatar>
                    <h4 class="subtitle-1 font-weight-medium mb-2">
                      Gerencie facilmente
                    </h4>
                    <p class="caption grey--text text--darken-1">
                      Cancele ou modifique suas linhas quando quiser
                    </p>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Botão de ação fixo -->
    <div
      v-if="selectedPlans.length > 0 && !confirmationModal"
      class="floating-action-container"
    >
      <v-card class="floating-action-card elevation-8">
        <v-card-text class="pa-4">
          <div class="d-flex align-center justify-space-between">
            <div class="action-summary">
              <div class="font-weight-bold">
                {{
                  `${selectedPlans.length} ${
                    selectedPlans.length > 1
                      ? "linhas selecionadas"
                      : "linha selecionada"
                  }`
                }}
              </div>
              <div class="caption grey--text">
                Total: {{ formatCurrency(totalMonthly) }}/mês
              </div>
            </div>
            <v-btn
              color="primary"
              large
              class="action-button"
              :loading="loading"
              @click="openConfirmationModal"
            >
              <v-icon left>mdi-clipboard-check</v-icon>
              Revisar e Contratar
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Modal de Confirmação -->
    <v-dialog v-model="confirmationModal" max-width="800">
      <v-card>
        <v-card-title class="text-h5 primary--text">
          <v-icon left color="primary">mdi-check-circle</v-icon>
          Confirmar Contratação
        </v-card-title>

        <v-card-text class="pa-6">
          <!-- Planos Selecionados -->
          <div class="mb-4">
            <h3 class="subtitle-1 font-weight-bold mb-3">
              Linhas de Rastreio Selecionadas ({{ selectedPlans.length }})
            </h3>
            <v-row>
              <v-col
                v-for="plan in selectedPlans"
                :key="plan.idPaypal"
                cols="12"
                sm="6"
              >
                <v-card outlined class="mb-2">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center">
                      <div
                        class="plan-icon mr-3"
                        :style="{ background: plan.iconColor || '#1AE06E' }"
                      >
                        <v-icon color="white" size="20">{{ plan.icon }}</v-icon>
                      </div>
                      <div>
                        <div class="font-weight-medium">{{ plan.name }}</div>
                        <div class="caption primary--text font-weight-bold">
                          {{ formatCurrency(plan.price) }}/mês
                        </div>
                        <div
                          v-if="plan.trialDays && plan.trialDays > 0"
                          class="caption success--text"
                        >
                          🎁 {{ plan.trialDays }} dias grátis
                        </div>
                        <div
                          v-if="plan.trialDays && plan.trialDays > 0"
                          class="caption grey--text"
                        >
                          Cobrança inicia: {{ getIndividualTrialEndDate(plan) }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Resumo de Cobrança -->
          <div class="billing-confirmation">
            <v-divider class="mb-4"></v-divider>
            <h3 class="subtitle-1 font-weight-bold mb-3">Resumo da Cobrança</h3>

            <!-- Trial info -->
            <div v-if="hasAnySelectedPlanWithTrial" class="mb-4">
              <v-alert color="success" outlined icon="mdi-gift" dense>
                <div class="font-weight-medium">
                  🎉 Período de teste gratuito disponível!
                </div>
                <div class="caption">
                  {{ getTrialSummary() }}
                </div>
              </v-alert>
            </div>

            <!-- Cobrança detalhada por tipo de plano -->
            <div v-if="hasPlansWithoutTrial" class="mb-3">
              <div
                class="billing-row d-flex justify-space-between align-center py-3"
              >
                <div>
                  <div class="body-2 font-weight-medium pb-2 orange--text">
                    💳 Cobrança imediata
                  </div>
                  <div class="caption grey--text">
                    {{ getPlansWithoutTrialNames() }} - {{ remainingDays }} dias
                    restantes do ciclo atual
                  </div>
                </div>
                <div class="text-right">
                  <div class="body-1 font-weight-bold primary--text">
                    {{ formatCurrency(getImmediateBillingValue()) }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="hasPlansWithTrial" class="mb-3">
              <div
                class="billing-row d-flex justify-space-between align-center py-3"
              >
                <div>
                  <div class="body-2 font-weight-medium pb-2">
                    🎁 Período Gratuito
                  </div>
                  <div class="caption grey--text">
                    {{ getPlansWithTrialNames() }} - Cobrança inicia
                    {{ getTrialEndDescription() }}
                  </div>
                  <div class="caption primary--text font-weight-medium">
                    Após período de avaliação: {{ getTrialBillingText() }}
                  </div>
                </div>
                <div class="text-right">
                  <div class="body-1 font-weight-bold success--text">
                    Grátis agora
                  </div>
                </div>
              </div>
            </div>

            <!-- Fallback para quando todos são do mesmo tipo -->
            <div
              v-if="!hasPlansWithTrial && !hasPlansWithoutTrial"
              class="mb-3"
            >
              <div
                class="billing-row d-flex justify-space-between align-center py-3"
              >
                <div>
                  <div class="body-2 font-weight-medium pb-2">
                    Cobrança do ciclo atual
                  </div>
                  <div class="caption grey--text">
                    {{ getBillingDescription() }}
                  </div>
                </div>
                <div class="text-right">
                  <div
                    class="body-1 font-weight-bold"
                    :class="getCurrentCycleValueClass()"
                  >
                    {{ getCurrentCycleValue() }}
                  </div>
                </div>
              </div>
            </div>

            <v-divider class="my-3"></v-divider>

            <!-- Próximos meses -->
            <div
              class="billing-row d-flex justify-space-between align-center py-3"
            >
              <div>
                <div class="body-2 font-weight-medium pb-2">Próximos meses</div>
                <div class="caption grey--text">
                  Junto com sua assinatura principal
                </div>
              </div>
              <div class="text-right">
                <div class="headline font-weight-bold primary--text">
                  {{ formatCurrency(totalMonthly) }}/mês
                </div>
              </div>
            </div>

            <v-divider class="my-3"></v-divider>

            <!-- Informações importantes -->
            <div class="">
              <v-alert
                color="info"
                outlined
                icon="mdi-information"
                dense
                style="margin-bottom: -4px"
              >
                <div class="caption" style="margin-top: -17px">
                  <span v-if="hasPlansWithTrial && hasPlansWithoutTrial">
                    <br />⌛ Linhas com período gratuito iniciarão a cobrança
                    apenas após o término do período de avaliação. <br />💳
                    Linhas sem período de avaliação serão cobradas imediatamente
                    de forma proporcional.
                  </span>
                  <span v-else-if="hasPlansWithTrial">
                    <br />⌛ As assinaturas adicionais iniciarão a cobrança
                    apenas após o período de avaliação gratuito.
                  </span>
                  <span v-else>
                    <br />💳 As assinaturas adicionais serão cobradas
                    imediatamente de forma proporcional ao ciclo atual do plano
                    principal.
                  </span>
                  <br />🔗 Vinculada ao Plano Principal Atenção: Se o teste do
                  Plano Profissional terminar e não for renovado, ou se você
                  cancelar o Plano Profissional, as Linhas Adicionais serão
                  automaticamente desativadas, mesmo que ainda restem dias de
                  uso grátis nelas. <br />✅ Para manter a linha adicional
                  ativa, é necessário manter o Plano Profissional vigente.
                </div>
              </v-alert>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn color="grey" text @click="confirmationModal = false">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            :loading="loading"
            @click="confirmAndStartTrial"
          >
            <v-icon left>mdi-check</v-icon>
            {{ getActionButtonText() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  getAddonPlans,
  renewLoggedUser,
  getCurrentSubscriptionData,
  createStripeAddonSubscription,
} from "@/services/external/paypal-service";
import dayjs from "dayjs";
import db from "@/database";

import "./PlanAddons.css";

export default {
  name: "SubscriptionLines",
  components: {
    UserMenu: () => import("@/layouts/components/UserMenu"),
  },
  data() {
    return {
      loading: false,
      loadingPlans: true,
      selectedPlans: [],
      confirmationModal: false,
      // Dados da assinatura atual
      currentSubscription: null,
      remainingDays: 18,
      totalDaysInCycle: 30,
      availablePlans: [],
    };
  },
  computed: {
    totalMonthly() {
      return this.selectedPlans.reduce((total, plan) => {
        return total + parseFloat(plan.price.replace(",", "."));
      }, 0);
    },

    allSelectedPlansHaveTrial() {
      return (
        this.selectedPlans.length > 0 &&
        this.selectedPlans.every((plan) => plan.trialDays && plan.trialDays > 0)
      );
    },

    noSelectedPlanHasTrial() {
      return (
        this.selectedPlans.length > 0 &&
        this.selectedPlans.every(
          (plan) =>
            !plan.trialDays || plan.trialDays === 0 || plan.trialDays === null
        )
      );
    },

    hasAnySelectedPlanWithTrial() {
      return this.selectedPlans.some(
        (plan) => plan.trialDays && plan.trialDays > 0
      );
    },

    hasPlansWithTrial() {
      return this.selectedPlans.some(
        (plan) => plan.trialDays && plan.trialDays > 0
      );
    },

    hasPlansWithoutTrial() {
      return this.selectedPlans.some(
        (plan) =>
          !plan.trialDays || plan.trialDays === 0 || plan.trialDays === null
      );
    },

    plansWithTrial() {
      return this.selectedPlans.filter(
        (plan) => plan.trialDays && plan.trialDays > 0
      );
    },

    plansWithoutTrial() {
      return this.selectedPlans.filter(
        (plan) =>
          !plan.trialDays || plan.trialDays === 0 || plan.trialDays === null
      );
    },

    canSelectPlans() {
      return (
        this.currentSubscription &&
        !this.currentSubscription.isManualSubscription
      );
    },
  },
  created() {
    this.fetchAddonPlans();
  },
  methods: {
    async fetchAddonPlans() {
      this.loadingPlans = true;
      this.$store.dispatch("loading/openDialog");
      try {
        const currSubsctiptResp = await getCurrentSubscriptionData();
        this.currentSubscription = currSubsctiptResp.data;

        if (this.currentSubscription) {
          this.remainingDays =
            this.currentSubscription.remainingDaysInCycle || 18;
          this.totalDaysInCycle =
            this.currentSubscription.totalDaysInCurrentCycle || 30;
        }

        const mainSubInTrial = this.currentSubscription.status === "TRIALING";

        const response = await getAddonPlans();
        this.availablePlans = response.data.map((plan) => {
          let trialDays = plan.trialDays || 0;

          if (mainSubInTrial && trialDays < this.remainingDays) {
            trialDays = this.remainingDays;
          }

          return {
            ...plan,
            trialDays: trialDays,
          };
        });
      } catch (error) {
        this.$errorHandler(error);
        this.remainingDays = 18;
        this.totalDaysInCycle = 30;
        this.availablePlans = [];
      }

      this.loadingPlans = false;
      this.$store.dispatch("loading/closeDialog");
    },

    isPlanSelected(plan) {
      return this.selectedPlans.some(
        (selected) => selected.idPaypal === plan.idPaypal
      );
    },

    showManualSubscriptionAlert({
      title = "Assinatura via suporte",
      message = "Entre em contato com o suporte para contratar essa linha adicional.",
    }) {
      this.$infoAlert({
        title,
        message,
      });
    },

    isAddonInMainSubscription(plan) {
      if (
        this.currentSubscription &&
        this.currentSubscription.addonSubscriptions &&
        Array.isArray(this.currentSubscription.addonSubscriptions)
      ) {
        const result = this.currentSubscription.addonSubscriptions.some(
          (addon) => addon.planId === plan.idPaypal
        );
        return result;
      }
      return false;
    },

    togglePlanSelection(plan) {
      if (!!this.isAddonInMainSubscription(plan)) {
        this.showManualSubscriptionAlert({
          title: "Linha já contratada",
          message:
            "Essa linha adicional já faz parte da sua assinatura principal.",
        });
        return;
      }

      if (!this.canSelectPlans) {
        this.showManualSubscriptionAlert();
        return;
      }

      const isSelected = this.isPlanSelected(plan);

      if (isSelected) {
        this.selectedPlans = this.selectedPlans.filter(
          (selected) => selected.idPaypal !== plan.idPaypal
        );
      } else {
        this.selectedPlans.push(plan);
      }
    },

    openConfirmationModal() {
      this.confirmationModal = true;
    },

    async confirmAndStartTrial() {
      this.confirmationModal = false;
      await this.startTrial();
    },

    getCardStyle(plan) {
      const isSelected = this.isPlanSelected(plan);
      return {
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: isSelected ? "scale(1.02)" : "scale(1)",
        border: isSelected ? "2px solid #4caf50" : "2px solid transparent",
      };
    },

    formatDate(date, formatString = "DD/MM") {
      return dayjs(date).format(formatString);
    },

    async startTrial() {
      if (this.loading) return;

      this.loading = true;
      this.$store.dispatch("loading/openDialog");
      try {
        const subscriptionData = this.selectedPlans.map(
          (plan) => plan.idPaypal
        );

        await createStripeAddonSubscription(subscriptionData);

        const { data } = await renewLoggedUser();
        this.$store.commit("login/set", data);

        // Apagar no db os valores da coleção config para obrigar o app á fazer nova sincronia
        await db.open();
        await db.config.clear();

        this.$store.dispatch("loading/closeDialog");
        this.loading = false;

        this.$successAlert({
          title: "Planos adicionais contratados com sucesso!",
          message: "Comece a utilizar as funcionalidades agora mesmo",
        });

        this.$router.push({ name: "home" });
      } catch (error) {
        this.loading = false;
        this.$store.dispatch("loading/closeDialog");
        this.$errorHandler(error);
      }
    },

    formatCurrency(value) {
      if (!value) return "R$ 0,00";
      const numValue =
        typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(numValue);
    },

    // Novos métodos para a interface simplificada
    getTrialSummary() {
      if (this.allSelectedPlansHaveTrial) {
        return "Todos os itens selecionados têm período gratuito!";
      } else if (this.hasAnySelectedPlanWithTrial) {
        const withTrial = this.selectedPlans.filter(
          (p) => p.trialDays && p.trialDays > 0
        ).length;
        return `${withTrial} de ${this.selectedPlans.length} itens com período gratuito`;
      }
      return "";
    },

    getBillingDescription() {
      if (this.hasAnySelectedPlanWithTrial) {
        return "Cobrado após o período de teste";
      }
      return `Cobrado agora referente a ${this.remainingDays} dias restantes do ciclo atual`;
    },

    getCurrentCycleValue() {
      if (this.getImmediateBillingValue() > 0) {
        return this.formatCurrency(this.getImmediateBillingValue());
      }
      return "Grátis";
    },

    getCurrentCycleValueClass() {
      return this.getImmediateBillingValue() > 0
        ? "primary--text"
        : "success--text";
    },

    getActionButtonText() {
      if (this.allSelectedPlansHaveTrial) {
        return "Iniciar Teste Grátis";
      } else if (this.noSelectedPlanHasTrial) {
        return "Contratar Agora";
      } else {
        return "Continuar";
      }
    },

    getPlansWithTrialNames() {
      return this.plansWithTrial.map((plan) => plan.name).join(", ");
    },

    getPlansWithoutTrialNames() {
      return this.plansWithoutTrial.map((plan) => plan.name).join(", ");
    },

    getTrialEndDescription() {
      if (this.plansWithTrial.length === 0) return "";

      // Se todos os planos com trial têm o mesmo período
      const trialDays = this.plansWithTrial[0].trialDays;
      const allSameTrial = this.plansWithTrial.every(
        (plan) => plan.trialDays === trialDays
      );

      if (allSameTrial) {
        const endDate = dayjs().add(trialDays, "days");
        return `em ${trialDays} dias (${endDate.format("DD/MM/YYYY")})`;
      } else {
        return "conforme período de cada item";
      }
    },

    getTrialPlansValue() {
      return this.plansWithTrial.reduce((total, plan) => {
        const planPrice = parseFloat(plan.price.replace(",", "."));
        const dailyRate = planPrice / this.totalDaysInCycle;

        // Calcula os dias que serão cobrados após o trial no ciclo atual
        const daysAfterTrial = Math.max(0, this.remainingDays - plan.trialDays);

        return total + dailyRate * daysAfterTrial;
      }, 0);
    },

    getTrialBillingText() {
      const proportionalValue = this.getTrialPlansValue();

      if (proportionalValue > 0) {
        return `${this.formatCurrency(
          proportionalValue
        )} (proporcional ao ciclo atual)`;
      } else {
        return "Grátis até o próximo ciclo";
      }
    },

    getIndividualTrialEndDate(plan) {
      if (!plan.trialDays || plan.trialDays === 0) return "";
      const endDate = dayjs().add(plan.trialDays, "days");
      return endDate.format("DD/MM/YYYY");
    },

    getImmediateBillingValue() {
      return this.plansWithoutTrial.reduce((total, plan) => {
        const planPrice = parseFloat(plan.price.replace(",", "."));
        const dailyRate = planPrice / this.totalDaysInCycle;
        return total + dailyRate * this.remainingDays;
      }, 0);
    },
  },
};
</script>

<style scoped>
.background-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.subscription-container {
  position: relative;
  z-index: 2;
}

.plans-header {
  text-align: center;
  margin-bottom: 2rem;
}

.plan-card {
  position: relative;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  height: 100%;
}

.plan-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.selected-card {
  border: 2px solid #4caf50;
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.2);
}

.selection-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.info-indicator {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
}

.plan-icon-large {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.price-section {
  padding: 1rem 0;
}

.price-amount {
  font-size: 2rem;
  line-height: 1.2;
}

.features-preview {
  text-align: left;
  max-height: 120px;
  overflow: hidden;
}

.feature-item {
  font-size: 0.875rem;
  line-height: 1.4;
}

.tags-section {
  margin-top: auto;
  margin-bottom: 1rem;
}

.billing-row {
  min-height: 60px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  padding: 0 1rem;
  margin-bottom: 0.5rem;
}

.info-section {
  border-radius: 12px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.feature-step {
  padding: 1.5rem;
  text-align: center;
}

.floating-action-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem;
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.95) 70%,
    transparent
  );
}

.floating-action-card {
  max-width: 800px;
  margin: 0 auto;
  border-radius: 16px;
  background: white;
}

.action-summary {
  flex-grow: 1;
}

.action-button {
  border-radius: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.billing-confirmation {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.billing-confirmation .billing-row {
  background: white;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}

.plan-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 960px) {
  .floating-action-container {
    padding: 0.5rem;
  }

  .floating-action-card .action-button {
    min-width: 140px;
  }

  .plan-card {
    margin-bottom: 1rem;
  }
}

@media (max-width: 600px) {
  .floating-action-card .v-card-text {
    padding: 1rem !important;
  }

  .floating-action-card .d-flex {
    flex-direction: column;
    gap: 1rem;
  }

  .action-button {
    width: 100%;
  }
}
</style>
